import { type Cadre, dimensionsRedressees, echantillonner, redressement } from '@admin-pmvb/core'
import { COTE_ANALYSE, detecterPage } from './contour'

// Recadrage sur le téléphone : la photo est décodée par le navigateur (orientation EXIF
// appliquée, comme à l'écran), réduite dans un canevas, analysée (bords de la page), et
// l'aperçu de la page redressée est calculé sur une copie réduite. La photo elle-même n'est
// jamais modifiée : elle part entière, avec ses coins ; le serveur la redresse.

/** Grand côté de l'aperçu de la page redressée (vignette de la liste). */
const COTE_APERCU = 480
/** Copie de la photo où l'aperçu est lu : assez fine pour une vignette nette. */
const COTE_SOURCE_APERCU = 1200
/** Une photo que le navigateur ne décode pas dans ce délai reste entière. */
const DELAI_DECODAGE_MS = 15_000

async function chargerImage(url: string): Promise<HTMLImageElement> {
  const img = new Image()
  img.decoding = 'async'
  img.src = url
  let minuteur: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      img.decode(),
      new Promise((_, rejeter) => {
        minuteur = setTimeout(() => rejeter(new Error('décodage trop long')), DELAI_DECODAGE_MS)
      }),
    ])
  } finally {
    clearTimeout(minuteur)
  }
  return img
}

function pixels(img: HTMLImageElement, cote: number): ImageData | null {
  const echelle = Math.min(1, cote / Math.max(img.naturalWidth, img.naturalHeight))
  const canevas = document.createElement('canvas')
  canevas.width = Math.max(1, Math.round(img.naturalWidth * echelle))
  canevas.height = Math.max(1, Math.round(img.naturalHeight * echelle))
  const ctx = canevas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(img, 0, 0, canevas.width, canevas.height)
  return ctx.getImageData(0, 0, canevas.width, canevas.height)
}

async function dessinerApercu(img: HTMLImageElement, cadre: Cadre): Promise<string | null> {
  const source = pixels(img, COTE_SOURCE_APERCU)
  const h = redressement(cadre, 1)
  if (!source || !h) return null
  const plein = dimensionsRedressees(cadre, source.width, source.height)
  const f = Math.min(1, COTE_APERCU / Math.max(plein.largeur, plein.hauteur))
  const largeur = Math.max(1, Math.round(plein.largeur * f))
  const hauteur = Math.max(1, Math.round(plein.hauteur * f))
  const page = echantillonner(
    { largeur: source.width, hauteur: source.height, donnees: source.data },
    h,
    largeur,
    hauteur,
  )
  const canevas = document.createElement('canvas')
  canevas.width = largeur
  canevas.height = hauteur
  const ctx = canevas.getContext('2d')
  if (!ctx) return null
  ctx.putImageData(new ImageData(page, largeur, hauteur), 0, 0)
  const blob = await new Promise<Blob | null>((r) => canevas.toBlob(r, 'image/jpeg', 0.85))
  return blob ? URL.createObjectURL(blob) : null
}

/**
 * Cherche les bords de la page sur la photo (URL d'objet) et, s'ils sont trouvés, l'aperçu de
 * la page redressée (URL d'objet, à révoquer). Null si le navigateur ne sait pas lire la photo
 * (HEIC hors Safari) : elle partira entière.
 */
export async function analyserPhoto(
  url: string,
): Promise<{ proposition: Cadre | null; apercu: string | null } | null> {
  try {
    const img = await chargerImage(url)
    const d = pixels(img, COTE_ANALYSE)
    if (!d) return null
    const proposition = detecterPage({ largeur: d.width, hauteur: d.height, donnees: d.data })
    return { proposition, apercu: proposition ? await dessinerApercu(img, proposition) : null }
  } catch {
    return null
  }
}

/** Aperçu de la page redressée selon des coins ajustés à la main ; null en cas d'échec. */
export async function apercuRedresse(url: string, cadre: Cadre): Promise<string | null> {
  try {
    return await dessinerApercu(await chargerImage(url), cadre)
  } catch {
    return null
  }
}
